import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, Check, Sparkles, Trophy, Coins, Package, Flame, Award } from "lucide-react";
import { ITEM_ICON, itemName, SKILL_LABEL } from "@/lib/gameData";
import { useGame } from "@/context/GameContext";

function fmtRange(start, end) {
  const s = new Date(start), e = new Date(end);
  const opts = { month: "short", day: "numeric" };
  return `${s.toLocaleDateString(undefined, opts)} — ${e.toLocaleDateString(undefined, opts)}`;
}

function daysLeft(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function StatusBadge({ status }) {
  const cfg = {
    active:   { text: "Active",   cls: "bg-emerald-900/40 border-emerald-700 text-emerald-300" },
    upcoming: { text: "Upcoming", cls: "bg-amber-900/40 border-amber-700 text-amber-300" },
    ended:    { text: "Ended",    cls: "bg-stone-900 border-stone-700 text-stone-500" },
  }[status] || { text: status, cls: "bg-stone-900 border-stone-700 text-stone-500" };
  return (
    <span className={"text-[10px] font-numeric uppercase tracking-widest border rounded px-1.5 py-0.5 " + cfg.cls}>
      {cfg.text}
    </span>
  );
}

function RewardChips({ reward, contentItems }) {
  if (!reward) return null;
  const chips = [];
  if (reward.gold) chips.push({ icon: Coins, label: `${reward.gold} gp` });
  for (const [sk, amt] of Object.entries(reward.xp || {})) {
    chips.push({ icon: Sparkles, label: `+${amt} ${SKILL_LABEL[sk] || sk} XP` });
  }
  for (const [iid, iq] of reward.items || []) {
    chips.push({ icon: Package, label: `${iq}× ${itemName(iid, contentItems)}` });
  }
  if (reward.title) {
    chips.push({ icon: Award, label: `Title: ${reward.title}` });
  }
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((c, i) => {
        const Icon = c.icon;
        return (
          <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-900/70 bg-amber-950/40 text-amber-200 font-numeric text-[10px]">
            <Icon size={11} /> {c.label}
          </span>
        );
      })}
    </div>
  );
}

function TaskRow({ ev, task, contentItems, onClaim }) {
  const pct = task.target > 0 ? Math.min(100, Math.round((Math.min(task.current, task.target) / task.target) * 100)) : 100;
  return (
    <div className="panel-inset p-3 space-y-2" data-testid={`event-task-${ev.id}-${task.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-sm text-stone-100 truncate">{task.objective}</div>
          <div className="text-[11px] italic text-stone-500 font-body">{task.narration}</div>
        </div>
        {task.claimed ? (
          <span className="text-[10px] uppercase tracking-widest font-numeric text-emerald-400 shrink-0 flex items-center gap-1">
            <Check size={12} /> Claimed
          </span>
        ) : (
          <button
            onClick={() => onClaim(ev.id, task.id, false)}
            disabled={!task.done || ev.status !== "active"}
            className={
              "text-[10px] px-2 py-1 rounded border font-body shrink-0 transition-colors " +
              (task.done && ev.status === "active"
                ? "bg-amber-800 border-amber-600 text-amber-50 hover:bg-amber-700"
                : "bg-stone-900 border-stone-700 text-stone-500 cursor-not-allowed")
            }
            data-testid={`claim-task-${task.id}`}
          >
            Claim
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 text-[10px] font-numeric text-stone-400">
        <div className="flex-1 xp-bar-outer" style={{ height: 5 }}>
          <div
            className="xp-bar-fill"
            style={{
              width: `${pct}%`,
              background: task.done ? "linear-gradient(180deg, #10b981 0%, #065f46 100%)" : undefined,
            }}
          />
        </div>
        <span className={task.done ? "text-emerald-300" : "text-amber-300"}>
          {Math.min(task.current, task.target)}/{task.target}
        </span>
      </div>
      <RewardChips reward={task.reward} contentItems={contentItems} />
    </div>
  );
}

function EventCard({ ev, contentItems, onClaim }) {
  const isActive = ev.status === "active";
  return (
    <article className="panel overflow-hidden" data-testid={`event-card-${ev.id}`}>
      <div
        className="relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.55), rgba(12,10,9,0.92)), url(${ev.banner})`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="p-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Flame size={14} className="text-amber-400" />
              <h2 className="font-display text-2xl heading-gilded truncate">{ev.title}</h2>
              <StatusBadge status={ev.status} />
            </div>
            <div className="text-[11px] font-numeric text-stone-400 uppercase tracking-widest">
              {ev.season} · {fmtRange(ev.start_iso, ev.end_iso)}
              {isActive && <span className="ml-2 text-amber-300">· {daysLeft(ev.end_iso)}d left</span>}
              {ev.status === "upcoming" && <span className="ml-2 text-amber-300">· starts in {daysLeft(ev.start_iso)}d</span>}
            </div>
            <p className="mt-2 text-stone-300 font-body italic text-sm max-w-2xl">&ldquo;{ev.summary}&rdquo;</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500">Tasks</h3>
        <div className="space-y-2">
          {ev.tasks.map((t) => (
            <TaskRow key={t.id} ev={ev} task={t} contentItems={contentItems} onClaim={onClaim} />
          ))}
        </div>

        {/* Grand reward */}
        <div className={"panel-inset p-3 flex items-center gap-3 " + (ev.all_done ? "border-amber-500" : "opacity-80")}>
          <div className="w-10 h-10 rounded bg-amber-900/40 border border-amber-700 flex items-center justify-center">
            <Trophy size={16} className="text-amber-300" />
          </div>
          <div className="flex-1">
            <div className="font-display text-sm text-stone-100">Grand Reward</div>
            <div className="text-[11px] italic text-stone-500 font-body">Complete every task to claim.</div>
            <div className="mt-1"><RewardChips reward={ev.grand_reward} contentItems={contentItems} /></div>
          </div>
          {ev.grand_claimed ? (
            <span className="text-[10px] uppercase tracking-widest font-numeric text-emerald-400 shrink-0 flex items-center gap-1">
              <Check size={12} /> Claimed
            </span>
          ) : (
            <button
              onClick={() => onClaim(ev.id, null, true)}
              disabled={!ev.all_done || !isActive}
              className={
                "text-[10px] px-3 py-1.5 rounded border font-body shrink-0 transition-colors " +
                (ev.all_done && isActive
                  ? "bg-amber-800 border-amber-600 text-amber-50 hover:bg-amber-700"
                  : "bg-stone-900 border-stone-700 text-stone-500 cursor-not-allowed")
              }
              data-testid={`claim-grand-${ev.id}`}
            >
              Claim
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export function EventsPanel() {
  const { content, refresh: refreshGame } = useGame();
  const [payload, setPayload] = useState(null);
  const contentItems = content?.items || {};

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/api/game/social/events");
      setPayload(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, [refresh]);

  async function claim(event_id, task_id, grand) {
    try {
      await api.post("/api/game/social/events/claim", { event_id, task_id, grand });
      toast.success(grand ? "Grand reward claimed!" : "Reward claimed.");
      refresh();
      if (refreshGame) refreshGame();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const grouped = useMemo(() => {
    if (!payload) return { active: [], upcoming: [], ended: [] };
    return payload.events.reduce((acc, ev) => {
      (acc[ev.status] ||= []).push(ev);
      return acc;
    }, { active: [], upcoming: [], ended: [] });
  }, [payload]);

  if (!payload) {
    return <div className="p-6 text-stone-500 italic font-body">Reading the seasonal ledger…</div>;
  }

  return (
    <div className="p-4 lg:p-6 space-y-3" data-testid="events-panel">
      <div
        className="panel overflow-hidden relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.55), rgba(12,10,9,0.92)), url(/generated/hero_landing.png)`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="p-6 flex items-center gap-6">
          <div className="w-20 h-20 rounded-lg border-2 border-amber-700 bg-stone-950 flex items-center justify-center">
            <CalendarClock size={32} className="text-amber-400" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-4xl heading-gilded">Seasonal Events</h1>
            <p className="text-stone-400 font-body italic text-sm">
              The world remembers the turning of the year. Some tasks are only offered now.
            </p>
            <div className="flex flex-wrap gap-4 mt-2 text-sm font-numeric text-stone-300">
              <span>Active · <span className="text-emerald-300">{grouped.active.length}</span></span>
              <span>Upcoming · <span className="text-amber-300">{grouped.upcoming.length}</span></span>
            </div>
          </div>
        </div>
      </div>

      {grouped.active.length === 0 && grouped.upcoming.length === 0 && (
        <div className="panel p-6 text-center text-stone-500 italic font-body">
          No events on the horizon. Check back next season.
        </div>
      )}
      {[...grouped.active, ...grouped.upcoming, ...grouped.ended].map((ev) => (
        <EventCard key={ev.id} ev={ev} contentItems={contentItems} onClaim={claim} />
      ))}
    </div>
  );
}
